'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function() { 
        window.dataLayer.push(arguments); 
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false // We'll handle page views manually
      });
    }

    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', { 
      page_path: url,
      page_title: document.title
    });
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// Conversion tracking helpers
export function trackConversion(conversionType: 'appointment' | 'contact' | 'inquiry', data: Record<string, any>) {
  trackEvent('conversion', {
    conversion_type: conversionType,
    ...data
  });
}

export function trackFormStep(formName: string, step: number, stepName: string) {
  trackEvent('form_step', {
    form_name: formName,
    step_number: step,
    step_name: stepName
  });
}

export function trackInquiryType(inquiryType: string) {
  trackEvent('inquiry_type_selected', {
    inquiry_type: inquiryType,
    category: 'lead_qualification'
  });
}

export function trackAppointmentBooking(serviceType: string, appointmentDate: string) {
  trackEvent('appointment_booked', {
    service_type: serviceType,
    appointment_date: appointmentDate,
    category: 'conversion'
  });
  
  // Also track as conversion
  trackConversion('appointment', {
    service_type: serviceType,
    value: 1
  });
}

export function trackContactFormSubmission(inquiryType: string, subject: string) {
  trackEvent('contact_form_submitted', {
    inquiry_type: inquiryType,
    subject: subject,
    category: 'conversion'
  });
  
  // Also track as conversion
  trackConversion('contact', {
    inquiry_type: inquiryType,
    value: 1
  });
}

export function trackLeadSource(source: string, medium: string = '', campaign: string = '') {
  trackEvent('lead_source', {
    source: source,
    medium: medium,
    campaign: campaign,
    category: 'attribution'
  });
}

export function trackServiceInteraction(serviceName: string, interactionType: string) {
  trackEvent('service_interaction', {
    service_name: serviceName,
    interaction_type: interactionType,
    category: 'engagement'
  });
}